import React from "react"
import { observer } from "mobx-react"
import styles from "./EmployerList.module.scss"
import { columns } from "./columns"
import { saveAgGridColumnSize } from "modules/common/services/table/size-storage"
import { DefaultAgGridOptions, rowIdBy } from "modules/common/services/table/helpers";
import { EmployeeListType, EmployeeList } from "../../models/employer-list"
import {
  GetContextMenuItemsParams,
  GridReadyEvent,
  CellContextMenuEvent,
  GridOptions,
  IRowNode,
  RowDoubleClickedEvent,
  PostSortRowsParams,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

import { agGridLocale } from "modules/root/services/locale"
import { EmployerSnapshotType, EmployerType, fired, formatEmployer } from "../../models/employer"
import { agGridEntityContextMenu } from "modules/common/components/table/EntityContextMenu"
import copyToClipboard from "copy-to-clipboard"
import { GridTheme } from "modules/common/components/table/GridTheme"
import { MAX_INT } from "modules/common/constants"
import { RemoveConfirmation } from "modules/common/components/form/RemoveConfirmation"
import { routes } from "modules/common/routes"
import { ProjectPortfolioDictionaryType } from 'modules/dictionaries/project-portfolios/models/project-portfolio-dictionary'

const TABLE_NAME = EmployeeList.name
const sizeSaver = saveAgGridColumnSize(TABLE_NAME)

class Internal extends React.Component<EmployeeListProps> {
  private options: GridOptions
  private confirmRemoving?: (e: EmployerSnapshotType) => void
  private cols: any[] = [];

  constructor(props: any) {
    super(props)

    this.options = {
      ...DefaultAgGridOptions(true),
      getRowClass: (params) => (fired(params.data) ? "removed" : ""),
      postSortRows: this.postSort,
    }
    const config = columns(TABLE_NAME, props.portfolios.portfolios)

    this.cols = config
  }

  render() {
    const { store, hidden } = this.props

    return (
      <RemoveConfirmation<EmployerSnapshotType>
        observation={store.data}
        what={(e) => `сотрудника ${e.name}`}
        onConfirmed={this.onRemove}
        render={({ confirmRemoving }) => {
          this.confirmRemoving = confirmRemoving

          return (
            <GridTheme bordered={true} className={`designed ${styles.grid} ${hidden ? styles.hidden : ""}`}>
              <AgGridReact
                rowStyle={{
                  borderBottom: "1 px solid #EDF0F2",
                  fontFamily: "GothamPro, Arial, sans-serif",
                  color: "#00273D",

                  fontSize: "12px",
                }}
                rowClassRules={this.rowClassRules}
                rowSelection="single"
                suppressCellFocus={true}
                enableCellTextSelection={true}
                columnDefs={this.cols}
                rowData={store.data}
                localeText={agGridLocale}
                onRowDoubleClicked={this.onRowSelected}
                getRowId={getRowId}
                getContextMenuItems={this.getContextMenuItems}
                onGridReady={this.onGridReady}
                onCellContextMenu={this.onCellContextMenu}
                onSortChanged={store.sorter.resorted}
                onColumnResized={sizeSaver}
                gridOptions={this.options}
                rowBuffer={MAX_INT}
                context={this}
              />
            </GridTheme>
          )
        }}
      />
    )
  }

  private rowClassRules = {
    fired: (params: any) => {
      const data: EmployerSnapshotType = params.data
      return !!data.fireDate
    },
  };

  getContextMenuItems = ({ node }: GetContextMenuItemsParams) => {
    // меню вызвано не на строке — показывать нечего
    if (!node) {
      return [];
    }

    const { canChange, onEmployerSelected } = this.props

    return agGridEntityContextMenu({
      onChange: () => onEmployerSelected(getRowNodeId(node.data)),
      onCopy: canChange ? () => copyToClipboard(formatEmployer(node.data), { format: "text/plain" }) : undefined,
      onRemove: canChange ? () => this.confirmRemoving && this.confirmRemoving(node.data) : undefined,
    })
  };

  onCellContextMenu = ({ node }: CellContextMenuEvent) => {
    node && node.setSelected(true, true)
  };

  onRowSelected = ({ data }: RowDoubleClickedEvent) => {
    const { onEmployerSelected } = this.props
    onEmployerSelected(getRowNodeId(data))
  };

  onGridReady = async ({ api }: GridReadyEvent) => {
    const { sorter } = this.props.store
    setTimeout(() => api.applyColumnState(sorter.agGridSortState))
  };

  // с версии 28 postSort называется postSortRows и получает объект со строками
  postSort = ({ nodes: rowNodes }: PostSortRowsParams) => {
    // передвигаем уволенных сотрудников в конец

    function notFired({ data }: IRowNode) {
      return !fired(data)
    }

    function move(toIndex: number, fromIndex: number) {
      rowNodes.splice(toIndex, 0, rowNodes.splice(fromIndex, 1)[0])
    }

    let nextInsertPos = 0

    for (let i = 0; i < rowNodes.length; i++) {
      if (notFired(rowNodes[i])) {
        move(nextInsertPos, i)
        nextInsertPos++
      }
    }
  };

  /** Список заданий сотрудника по всем доскам, отфильтрованный по статусу бейджа */
  goToQuestions = (employer: EmployerType, status: string) => {
    window.open(`${routes.orders.orderQuestionsList()}#employerId=${employer.id}&status=${status}`, "_blank")
  };

  onRemove = (e: EmployerSnapshotType) => this.props.onEmployerRemove(e.id);
}

export const EmployerList = observer(Internal)

const getRowNodeId = (e: EmployerSnapshotType) => e.id
const getRowId = rowIdBy(getRowNodeId);

interface EmployeeListProps {
  onEmployerSelected: (id: string) => void
  onEmployerRemove: (id: string) => void
  store: EmployeeListType
  hidden?: boolean
  canChange: boolean
  portfolios: ProjectPortfolioDictionaryType
}
